// Checks that run before a Day Zero budget upload touches the database.
//
// Both came out of 10/8/2026: a sheet whose used range ran tens of thousands
// of rows past the data sent every blank row to be saved and crashed the write
// function, and the weekly version build started four seconds into an upload.

// Day Zero rows the app can accept in one upload. The largest real budget is
// about 1,700 lines; anything near this many is a sheet problem, not a budget.
export const MAX_UPLOAD_ROWS = 5000;

const blank = (v) => v === null || v === undefined || String(v).trim() === '';

// A row with no cost code, no cost type and no amount is a formatted empty
// row, not a budget line.
export const isBlankBudgetRow = (row) =>
    blank(row?.['Cost Code']) && blank(row?.['Cost Type']) && blank(row?.['Amount']);

export const dropBlankRows = (rows) => (rows || []).filter((r) => !isBlankBudgetRow(r));

// Version_Master rows (as readVersionTeamJob returns them). Returns null when
// the upload may go ahead, otherwise the sentence to show. `unlocked` is the
// admin unlock on the selected version (db/versionUnlock.js): an admin who
// opened an older version did so to load it, so a newer version is no reason
// to stop; a build in progress still is.
// A build takes about two hours; a version left in status 1 for longer than
// this is an abandoned build row (the test database has one from 2025), not a
// build that is running.
export const BUILD_WINDOW_HOURS = 6;

export const versionBuildBlock = (versions, selectedVersion, now = new Date(), { unlocked = false } = {}) => {
    const list = Array.isArray(versions) ? versions : [];
    const sel = Number(selectedVersion);
    const minutesAgo = (v) => {
        const started = v?.Version_Start_DateTime ? new Date(v.Version_Start_DateTime) : null;
        return started && !isNaN(started) ? (now - started) / 60000 : null;
    };
    const building = list.find((v) => {
        if (String(v?.Version_Status) !== '1' || v?.Version_Archived) return false;
        const mins = minutesAgo(v);
        return mins !== null && mins >= 0 && mins <= BUILD_WINDOW_HOURS * 60;
    });
    if (building) {
        const mins = Math.round(minutesAgo(building));
        const since = mins < 60 ? `${mins} min ago` : `${Math.round(mins / 60 * 10) / 10} h ago`;
        return `A new forecast version is being built right now (started ${since}). The build copies the version at its start, so an upload now would be left behind. Try again once it finishes; a build takes about two hours.`;
    }
    const newest = list.reduce((m, v) => Math.max(m, Number(v?.Version_GUID) || 0), 0);
    if (!unlocked && Number.isFinite(sel) && newest > sel) {
        return `A newer forecast version (${newest}) exists; this page is still on version ${sel}. Reload Fusion and upload again on the current version.`;
    }
    return null;
};
